import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { mutate } from 'swr';
import { AxiosError } from 'axios';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import TextButton from '@/components/@IntranetLibrary/Button/TextButton';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { CANCEL_APPROVAL_TYPES } from '@/constant';
import {
  APPROVAL_V2_URL,
  getApprovalSummarizeRequestUrl,
} from '@/constant/approval';
import { fetchApi } from '@/utils/api/customAxios';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import useHasRoles from '@/hooks/useHasRoles';
import useLayoutSize from '@/hooks/useLayoutSize';
import useApprovalType from '@/hooks/approval/useApprovalType';
import useCreateApproval from '@/hooks/approval/useCreateApproval';
import useSaveApprovalTypeTemplate from '@/hooks/approval/useSaveApprovalTypeTemplate';
import useUpdateApproval from '@/hooks/approval/useUpdateApproval';
import useApprovalRelationSync from '@/hooks/approval/useApprovalRelationSync';
import useDeactivatedApprovers from '@/hooks/approval/useDeactivatedApprovers';
import useDeactivatedUsers from '@/hooks/approval/useDeactivatedUsers';
import useMemberOrganizations from '@/hooks/approval/useMemberOrganizations';
import approvalFormToCreatePayload, {
  stripFontFamily,
} from '@/utils/approval/approvalFormToCreatePayload';
import { normalizeLang, pickI18nText } from '@/utils/approval/i18nText';
import {
  checkApprovalLineShortage,
  checkSubjectRequired,
  findSubmitViolation,
} from '@/utils/approval/submitChecks';
import { isSaVoucherApprovalType } from '@/utils/approval/saVoucher';
import { isShopManagerFeeType } from '@/utils/approval/shopManagerFee';
import {
  checkExpenseBeforeSave,
  isExpensePreApprovalType,
} from '@/utils/approval/expensePreApproval';
import { buildOpenConstructionCostContentHtml } from '@/components/Approval/@components/OpenConstructionCost';
import { buildOpenConstructionCostSettlementContentHtml } from '@/components/Approval/@components/OpenConstructionCostSettlement';
import {
  checkOpenConstructionCostBeforeSave,
  isOpenConstructionCostSettlementType,
  isOpenConstructionCostType,
} from '@/utils/approval/openConstructionCost';
import { buildFncoPreMarketingContentHtml } from '@/components/Approval/@components/FncoPreMarketing';
import { buildMarketingExpenseContentHtml } from '@/components/Approval/@components/MarketingExpense';
import {
  buildFncoPreMarketingAttributes,
  checkFncoPreMarketingBeforeSave,
  isFncoPreMarketingType,
} from '@/utils/approval/fncoPreMarketing';
import {
  buildMarketingExpenseAttributes,
  checkMarketingExpenseBeforeSave,
  isMarketingExpenseType,
} from '@/utils/approval/marketingExpense';
import {
  findEntExpenseViolation,
  isEntExpenseType,
} from '@/utils/approval/entExpense';
import {
  checkWorkSpecificationBeforeSave,
  isWorkSpecificationType,
} from '@/utils/approval/workSpecification';
import {
  FNCO_SHOW_ROOM_MISSING_MESSAGE,
  isFncoShowRoomType,
} from '@/utils/approval/fncoShowRoom';
import {
  FNCO_PRODUCT_PURCHASE_MISSING_MESSAGE,
  isFncoProductPurchaseType,
} from '@/utils/approval/fncoProductPurchase';
import {
  checkVehicleCostBeforeSave,
  isVehicleCostType,
} from '@/utils/approval/vehicleCost';
import {
  buildResignationAttributes,
  checkResignationBeforeSave,
  isResignationType,
  normalizeResignationState,
} from '@/utils/approval/resignation';
import { buildResignationContentHtml } from '@/components/Approval/@components/Resignation';
import {
  buildNewCorporateCardAttributes,
  checkNewCorporateCardBeforeSave,
  isNewCorporateCardType,
} from '@/utils/approval/newCorporateCard';
import { buildNewCorporateCardContentHtml } from '@/components/Approval/@components/NewCorporateCard';
import {
  buildBpAttributes,
  checkBpBeforeSave,
  isBpRegistrationType,
  normalizeBpState,
} from '@/utils/approval/businessPartnerRegistration';
import { buildBpContentHtml } from '@/components/Approval/@components/BusinessPartnerRegistration';
import {
  buildSapContractFeeAttributes,
  isSapContractFeeType,
} from '@/utils/approval/sapContractFee';
import { buildSapContractFeeContentHtml } from '@/components/Approval/@components/SapContractFee';
import {
  buildSerpStockAdjustmentAttributes,
  checkSerpStockAdjustmentBeforeSave,
  isSerpStockAdjustmentType,
} from '@/utils/approval/serpStockAdjustment';
import { buildSerpStockAdjustmentContentHtml } from '@/components/Approval/@components/SerpStockAdjustment';
import {
  buildSerpStoreChangeAttributes,
  checkSerpStoreChangeBeforeSave,
  isSerpStoreChangeType,
} from '@/utils/approval/serpStoreChange';
import { buildSerpStoreChangeContentHtml } from '@/components/Approval/@components/SerpStoreChange';
import {
  buildSerpStoreOpenAttributes,
  checkSerpStoreOpenBeforeSave,
  isSerpStoreOpenType,
} from '@/utils/approval/serpStoreOpen';
import { buildSerpStoreOpenContentHtml } from '@/components/Approval/@components/SerpStoreOpen';
import {
  buildSerpStoreReturnAttributes,
  checkSerpStoreReturnBeforeSave,
  isSerpStoreReturnType,
} from '@/utils/approval/serpStoreReturn';
import { buildSerpStoreReturnContentHtml } from '@/components/Approval/@components/SerpStoreReturn';
import {
  buildSerpLendingDelayAttributes,
  checkSerpLendingDelayBeforeSave,
  isSerpLendingDelayType,
} from '@/utils/approval/serpLendingDelay';
import { buildSerpLendingDelayContentHtml } from '@/components/Approval/@components/SerpLendingDelay';
import {
  buildSerpStoreClosureAttributes,
  checkSerpStoreClosureBeforeSave,
  isSerpStoreClosureType,
} from '@/utils/approval/serpStoreClosure';
import { buildSerpStoreClosureContentHtml } from '@/components/Approval/@components/SerpStoreClosure';
import {
  buildSerpIntermediaryReplaceAttributes,
  checkSerpIntermediaryReplaceBeforeSave,
  isSerpIntermediaryReplaceType,
} from '@/utils/approval/serpIntermediaryReplace';
import { buildSerpIntermediaryReplaceContentHtml } from '@/components/Approval/@components/SerpIntermediaryReplace';
import {
  buildSerpSellingPriceAttributes,
  checkSerpSellingPriceBeforeSave,
  isSerpSellingPriceType,
} from '@/utils/approval/serpSellingPrice';
import { buildSerpSellingPriceContentHtml } from '@/components/Approval/@components/SerpSellingPrice';
import {
  buildSerpSeasonPlanAttributes,
  checkSerpSeasonPlanBeforeSave,
  isSerpSeasonPlanType,
} from '@/utils/approval/serpSeasonPlan';
import { buildSerpSeasonPlanContentHtml } from '@/components/Approval/@components/SerpSeasonPlan';
import {
  buildSerpBulkStockAdjustmentAttributes,
  checkSerpBulkStockAdjustmentBeforeSave,
  isSerpBulkStockAdjustmentType,
} from '@/utils/approval/serpBulkStockAdjustment';
import { buildSerpBulkStockAdjustmentContentHtml } from '@/components/Approval/@components/SerpBulkStockAdjustment';
import {
  buildSerpSeasonEndAttributes,
  checkSerpSeasonEndBeforeSave,
  isSerpSeasonEndType,
} from '@/utils/approval/serpSeasonEnd';
import { buildSerpSeasonEndContentHtml } from '@/components/Approval/@components/SerpSeasonEnd';
import {
  buildSerpApStockTransferAttributes,
  checkSerpApStockTransferBeforeSave,
  isSerpApStockTransferType,
} from '@/utils/approval/serpApStockTransfer';
import { buildSerpApStockTransferContentHtml } from '@/components/Approval/@components/SerpApStockTransfer';
import {
  checkMortgageFeeBeforeSave,
  isMortgageFeeType,
} from '@/utils/approval/mortgageFee';
import {
  checkStoreOpenBeforeSave,
  isStoreOpenType,
} from '@/utils/approval/storeOpen';
import {
  checkCorporateCardRequestBeforeSave,
  isCorporateCardRequestType,
} from '@/utils/approval/corporateCardRequest';
import {
  checkAnnualLeaveRequestBeforeSave,
  isAnnualLeaveRequestType,
} from '@/utils/approval/annualLeaveRequest';
import {
  buildLongServiceLeaveAttributes,
  checkLongServiceLeaveBeforeSave,
  isLongServiceLeaveType,
  isPledgeMissing,
} from '@/utils/approval/longServiceLeave';
import { buildLongServiceLeaveContentHtml } from '@/components/Approval/@components/LongServiceLeave';
import { LONG_SERVICE_PLEDGE_ANCHOR_ID } from './LongServiceLeaveField';
import {
  checkProbationEvaluationBeforeSave,
  isProbationEvaluationType,
} from '@/utils/approval/probationEvaluation';
import {
  buildSalesBrandPermissionAttributes,
  checkSalesBrandPermissionBeforeSave,
  isSalesBrandPermissionType,
} from '@/utils/approval/salesBrandPermission';
import { toVehicleCostEmployee } from '@/utils/approval/vehicleCost';
import { buildSalesBrandPermissionContentHtml } from '@/components/Approval/@components/SalesBrandPermission';
import {
  buildSapBondStatusAttributes,
  formatSapBondStatusPeriod,
  isSapBondStatusType,
} from '@/utils/approval/sapBondStatus';
import { buildSapBondStatusContentHtml } from '@/components/Approval/@components/SapBondStatus';
import {
  buildSaTravelExpenseAttributes,
  isSaTravelRequestType,
  isSaTravelType,
} from '@/utils/approval/saTravelExpense';
import { buildSaTravelExpenseContentHtml } from '@/components/Approval/@components/SaTravelExpense';
import { buildSaTravelRequestContentHtml } from '@/components/Approval/@components/SaTravelExpense/SaTravelRequest';
import {
  buildFncoPriceSupplyAttributes,
  checkFncoPriceSupplyBeforeSave,
  getFncoPriceSupplyItems,
  isFncoPriceType,
} from '@/utils/approval/fncoPriceSupply';
import { buildFncoPriceSupplyContentHtml } from '@/components/Approval/@components/FncoPriceSupply';
import {
  buildSapCollateralChangeAttributes,
  isSapCollateralType,
} from '@/utils/approval/sapCollateralChange';
import { buildSapCollateralChangeContentHtml } from '@/components/Approval/@components/SapCollateralChange';
import {
  buildAccountingBpAttributes,
  isAccountingBpType,
} from '@/utils/approval/accountingBusinessPartner';
import { buildAccountingBpContentHtml } from '@/components/Approval/@components/AccountingBusinessPartner';
import {
  buildInfluencerMonthlyReportAttributes,
  isInfluencerMonthlyReportType,
} from '@/utils/approval/influencerMonthlyReport';
import { buildInfluencerMonthlyReportContentHtml } from '@/components/Approval/@components/InfluencerMonthlyReport';
import {
  buildProductionCostAttributes,
  checkProductionCostBeforeSave,
  isProductionCostType,
} from '@/utils/approval/productionCost';
import { buildProductionCostContentHtml } from '@/components/Approval/@components/ProductionCost';
import {
  buildExternalLoanAttributes,
  checkExternalLoanBeforeSave,
  isExternalLoanType,
} from '@/utils/approval/externalLoan';
import {
  buildInfluencerPplAttributes,
  isInfluencerPplType,
} from '@/utils/approval/influencerPpl';
import { buildInfluencerPplContentHtml } from '@/components/Approval/@components/InfluencerPpl';
import {
  buildSapBudgetIncreaseAttributes,
  findSelectedAccount,
  formatBudgetDepartment,
  isSapBudgetIncreaseType,
} from '@/utils/approval/sapBudgetIncrease';
import { buildSapBudgetIncreaseContentHtml } from '@/components/Approval/@components/SapBudgetIncrease';
import {
  checkDiscountVoucherBeforeSave,
  isDiscountVoucherType,
} from '@/utils/approval/discountVoucher';
import {
  findExpenseVoucherViolation,
  isExpenseVoucherType,
} from '@/utils/approval/expenseVoucher';
import { MyInfoState } from '@/recoil/atom';
import {
  checkGroupSalesBeforeSave,
  isGroupSalesType,
} from '@/utils/approval/groupSales';
import {
  checkGroupSalesMultiBeforeSave,
  isGroupSalesMultiType,
} from '@/utils/approval/groupSalesMulti';
import {
  checkMarketingCaseBeforeSave,
  isMarketingCaseType,
} from '@/utils/approval/marketingCase';
import {
  buildProductionVendorAttributes,
  isProductionVendorType,
} from '@/utils/approval/productionVendor';
import {
  buildProductLifecycleAttributes,
  isProductLifecycleType,
} from '@/utils/approval/productLifecycle';
import {
  buildStyleMasterPriceAttributes,
  isStyleMasterPriceType,
} from '@/utils/approval/styleMasterPrice';
import {
  buildProductionCostClosingAttributes,
  checkProductionCostClosingBeforeSave,
  isProductionCostClosingType,
} from '@/utils/approval/productionCostClosing';
import {
  buildClaimClosingAttributes,
  checkClaimClosingBeforeSave,
  isClaimClosingType,
} from '@/utils/approval/claimClosing';
import {
  buildInventoryReplenishmentAttributes,
  isInventoryReplenishmentType,
} from '@/utils/approval/inventoryReplenishment';
import {
  buildStorageMaterialOrderAttributes,
  checkStorageMaterialOrderBeforeSave,
  isStorageMaterialOrderType,
} from '@/utils/approval/storageMaterialOrder';
import {
  buildSupplierEvaluationAttributes,
  checkSupplierEvaluationBeforeSave,
  isSupplierEvaluationType,
} from '@/utils/approval/supplierEvaluation';
import {
  buildPurchaseRequisitionAttributes,
  checkPurchaseRequisitionBeforeSave,
  isPurchaseRequisitionType,
} from '@/utils/approval/purchaseRequisition';
import {
  buildCostQuotationAttributes,
  checkCostQuotationBeforeSave,
  isCostQuotationType,
} from '@/utils/approval/costQuotation';
import {
  buildSalesContractAttributes,
  isSalesContractType,
} from '@/utils/approval/salesContract';
import {
  buildCreditNoteAttributes,
  isCreditNoteType,
} from '@/utils/approval/creditNote';
import {
  buildClaimNoteAttributes,
  checkClaimNoteBeforeSave,
  isClaimNoteType,
} from '@/utils/approval/claimNote';
import { focusEntExpenseViolation } from './EntExpenseField';
import { focusExpenseVoucherField } from './ExpenseVoucherField';
import { isEmptyContent } from '@/utils/editor';
import { isRnWebView, RnMessage, sendToRN } from '@/utils/function';
import FileUploadIcon from '@/public/icons/approval/file-upload.svg';
import SendIcon from '@/public/icons/approval/send.svg';
import {
  approvalCreateApprovalLineAtom,
  approvalCreateDeactivatedNamesAtom,
  approvalCreateReferrersAtom,
  expenseVoucherInvalidFieldsAtom,
} from './atoms';
import { ActionButtonGroup, Footer, LeftActionStack } from './styles';
import type { ApprovalFormValues } from './types';

type CreateFooterProps = {
  apvType: number;
  /** 저장 대상 documentId — 있으면 PUT, 없으면 POST. 첫 POST 후 부모가 응답 docId 를 다시 내려줌 */
  documentId?: string;
  /**
   * 퇴사자 조회 대상 — 편집으로 연 문서만. 이 화면에서 처음 저장한 문서는 백엔드가 바로 못 찾아 422(Sentry 누적)
   */
  deactivatedCheckDocumentId?: string;
  /** 첫 POST 성공 시 응답 docId 를 부모로 전달 — 이후 저장이 PUT 으로 분기되도록 */
  onPersisted?: (docId: string) => void;
  /** 제공 시 제출 성공 처리를 호스트(모달)에 위임 — 페이지/앱 분기 생략 */
  onSubmitSuccess?: (docId: string) => void;
  /** 제공 시 작성취소/종료 처리를 호스트에 위임 — 페이지/앱 분기 생략 */
  onExit?: () => void;
  // 취소 버튼 이탈 시 확인 팝업 여부 판정(useExitGuard). create=작성내용 有 / edit=변경분 有.
  shouldWarnOnExit: () => boolean;
  /** 저장 전송 시점 폼 상태 캡처 — 성공 시 반환된 커밋을 호출해 이탈 가드 baseline 확정(useExitGuard) */
  captureSaveBaseline: () => () => void;
};

const APPROVAL_LIST_PATH = '/?tab=work';
const MIN_SUBMIT_LOCK_MS = 1000;

// [어드민] 시스템 관리자 역할. 어드민 콘솔에는 Portal_control_full_access 로 보이지만
// portal-user/me 가 주는 key 는 소문자 — 콘솔 표기대로 쓰면 항상 false.
const PORTAL_ADMIN_ROLE_KEY = 'portal_control_full_access';

const buildViewPath = (docId: string) => `/approval/viewer?doc=${docId}`;

/** 저장 실패 메시지 도출 — 편집(PUT)은 403/404/422 별도 문구, 그 외는 서버 message 우선 */
const messageForApiError = (
  error: unknown,
  fallback: string,
  isEditMode: boolean,
  t: (key: string) => string,
): string => {
  if (!(error instanceof AxiosError)) return fallback;
  const status = error.response?.status;
  const serverMessage = error.response?.data?.message;
  if (isEditMode) {
    if (status === 403) return t('본인이 작성한 문서만 편집할 수 있습니다.');
    if (status === 404) return t('문서를 찾을 수 없습니다.');
    if (status === 422)
      return serverMessage || t('편집 가능한 상태가 아닙니다.');
  }
  return serverMessage || fallback;
};

const extractResponseDocumentId = (response: unknown): string | null => {
  const pick = (o: unknown): string | null => {
    if (!o || typeof o !== 'object') return null;
    const r = o as Record<string, unknown>;
    for (const key of [
      'documentId',
      'docId',
      'documentNumber',
      'id',
    ] as const) {
      const v = r[key];
      if (typeof v === 'string' && v.length > 0) return v;
      if (typeof v === 'number') return String(v);
    }
    return null;
  };
  return pick(response) ?? pick((response as { data?: unknown })?.data);
};

// useApproval(문자열 key)과 useApprovalView(튜플 key)가 같은 문서를 따로 캐시하므로 predicate 로 둘 다 무효화한다.
// 재검증 GET 실패는 무시 — 캐시 갱신은 best-effort 이고, 저장 성공 판정을 뒤집으면 안 된다.
const invalidateDocumentCache = (docId: string) => {
  const baseKey = `${APPROVAL_V2_URL}/${docId}`;
  return mutate(
    (cacheKey) =>
      cacheKey === baseKey ||
      (Array.isArray(cacheKey) && cacheKey[0] === baseKey),
  ).catch(() => undefined);
};

// 저장된 내용으로 AI 요약 재생성 — 접수만 하고 바로 응답(조회 화면이 SSE 로 새 요약을 받음), 실패해도 저장은 성공
const requestSummary = (docId: string) =>
  fetchApi({
    method: 'POST',
    url: getApprovalSummarizeRequestUrl(docId),
  }).catch(() => undefined);

// 화면이 닫히며 요청이 취소되지 않게 대기 — 응답이 늦어도 상신 완료는 막지 않음
const SUMMARY_REQUEST_WAIT_MS = 3000;
const waitSummaryRequest = (request: Promise<unknown>) =>
  Promise.race([
    request,
    new Promise((resolve) => {
      setTimeout(resolve, SUMMARY_REQUEST_WAIT_MS);
    }),
  ]);

const CreateFooter = ({
  apvType,
  documentId,
  deactivatedCheckDocumentId,
  onPersisted,
  onSubmitSuccess,
  onExit,
  shouldWarnOnExit,
  captureSaveBaseline,
}: CreateFooterProps) => {
  const router = useRouter();
  const dialog = useDialog();
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();
  const { isMobile } = useLayoutSize();
  // 미로그인·조회 실패·응답 전에는 roles 가 빈 배열이라 false — 미노출이 기본값.
  // 권한 없어도 작성 화면 자체는 써야 하므로 훅 기본값인 리다이렉트는 끔.
  const isPortalAdmin = useHasRoles(PORTAL_ADMIN_ROLE_KEY, {
    redirectOnForbidden: false,
  });
  const { getValues, setValue } = useFormContext<ApprovalFormValues>();
  const approvalLine = useAtomValue(approvalCreateApprovalLineAtom);
  const setExpenseVoucherInvalid = useSetAtom(expenseVoucherInvalidFieldsAtom);
  const deactivatedNames = useAtomValue(approvalCreateDeactivatedNamesAtom);
  const { isLoading: isDeactivatedCheckLoading } = useDeactivatedApprovers(
    deactivatedCheckDocumentId,
  );
  const referrers = useAtomValue(approvalCreateReferrersAtom);
  // 상신 차단도 배지와 같은 퇴사 판정을 쓴다.
  // 결재라인 쪽은 ApprovalLineSection 이 판정해 deactivatedNames atom 으로
  // 넘겨주므로 이 컴포넌트는 그 응답이 도착했는지(로딩)만 기다리고,
  // 참조인 쪽은 넘겨주는 곳이 없어 이 컴포넌트가 직접 판정한다.
  const lineMemberIds = useMemo(
    () => [
      ...approvalLine.approvers.map((a) => a.passportId),
      ...(approvalLine.cooperation?.members.map((m) => m.passportId) ?? []),
    ],
    [approvalLine],
  );
  const referrerIds = useMemo(() => referrers.map((r) => r.id), [referrers]);
  const coApproverIds = useMemo(
    () => approvalLine.cooperation?.members.map((m) => m.passportId) ?? [],
    [approvalLine],
  );
  const memberOrganizations = useMemberOrganizations(lineMemberIds);
  // isLoading 이 아니라 isResolved 로 본다 — SWR 은 요청을 effect 에서 시작해
  // 키가 처음 생기는 렌더에서 isLoading 이 false 로 관측될 수 있어, 그 한 렌더에
  // '판정 없음' 이 '퇴사자 없음' 으로 통과한다.
  const { isResolved: lineActivationResolved, error: lineActivationError } =
    useDeactivatedUsers(lineMemberIds);
  const {
    deactivatedIds: deactivatedReferrerIds,
    isResolved: referrerActivationResolved,
    error: referrerActivationError,
  } = useDeactivatedUsers(referrerIds);
  const { create, isSubmitting: isCreating } = useCreateApproval();
  const { update, isSubmitting: isUpdating } = useUpdateApproval();
  const { saveTemplate } = useSaveApprovalTypeTemplate();
  // 확인 다이얼로그에 양식명 표기용 — 부모가 같은 키로 이미 구독 중이라 추가 요청 없음
  const { data: approvalType } = useApprovalType(
    isPortalAdmin ? apvType : null,
  );
  const syncRelations = useApprovalRelationSync();
  const [isSubmitFlowLocked, setIsSubmitFlowLocked] = useState(false);
  const [isDraftFlowLocked, setIsDraftFlowLocked] = useState(false);
  const [isTemplateFlowLocked, setIsTemplateFlowLocked] = useState(false);

  // PUT 경로(이미 저장된 문서) 여부 — documentId prop 유무로 판정.
  const isEditMode = Boolean(documentId);
  const isSubmitting = isCreating || isUpdating;
  const buttonsLocked = isSubmitting || isSubmitFlowLocked || isDraftFlowLocked;
  // 템플릿 저장은 문서 저장과 무관한 별도 요청 — 본 저장 버튼은 잠그지 않고 자기 자신만 잠금
  const templateButtonLocked = buttonsLocked || isTemplateFlowLocked;

  // 실패 후 최소 1초 lock 유지 — 연타 제출 방지(레거시 resetConfirmButtonAfterMin 패턴)
  const releaseSubmitLockAfterMin = useCallback(() => {
    setTimeout(() => setIsSubmitFlowLocked(false), MIN_SUBMIT_LOCK_MS);
  }, []);

  const buildResignationSnapshot = (
    state: ApprovalFormValues['resignation'],
  ) => {
    if (!isResignationType(apvType) || !state) return null;
    const normalized = normalizeResignationState(state);
    return buildResignationAttributes(
      normalized,
      buildResignationContentHtml(normalized, t),
    );
  };

  // 레거시 — 서약 미동의로 막히면 서약 영역에 안내를 띄우고 그 위치로 올림(임시저장·상신 공통)
  const focusLongServicePledge = () => {
    const current = getValues('longServiceLeave');
    if (current)
      setValue('longServiceLeave', { ...current, pledgeAlert: true });
    document
      .getElementById(LONG_SERVICE_PLEDGE_ANCHOR_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const buildLongServiceLeaveSnapshot = (
    state: ApprovalFormValues['longServiceLeave'],
  ) =>
    isLongServiceLeaveType(apvType) && state
      ? buildLongServiceLeaveAttributes(
          state,
          buildLongServiceLeaveContentHtml(state, t),
        )
      : null;

  const buildNewCorporateCardSnapshot = (
    state: ApprovalFormValues['newCorporateCard'],
  ) => {
    if (!isNewCorporateCardType(apvType) || !state) return null;
    return buildNewCorporateCardAttributes(
      state,
      buildNewCorporateCardContentHtml(state, t),
    );
  };

  const buildBpSnapshot = (state: ApprovalFormValues['bpRegistration']) => {
    if (!isBpRegistrationType(apvType) || !state) return null;
    const normalized = normalizeBpState(state);
    return buildBpAttributes(
      normalized,
      buildBpContentHtml(normalized, apvType, t),
      apvType,
      t,
    );
  };

  const buildSapContractFeeSnapshot = (
    state: ApprovalFormValues['sapContractFee'],
  ) => {
    if (!isSapContractFeeType(apvType) || !state?.loaded) return null;
    return buildSapContractFeeAttributes(
      state,
      buildSapContractFeeContentHtml(state.loaded.data, t),
    );
  };

  const buildSerpStoreOpenSnapshot = (
    state: ApprovalFormValues['serpStoreOpen'],
  ) => {
    if (!isSerpStoreOpenType(apvType) || !state?.source) return null;
    return buildSerpStoreOpenAttributes(
      state,
      buildSerpStoreOpenContentHtml(state.source, t),
    );
  };

  const buildSerpStoreReturnSnapshot = (
    state: ApprovalFormValues['serpStoreReturn'],
  ) => {
    if (!isSerpStoreReturnType(apvType) || !state?.source) return null;
    return buildSerpStoreReturnAttributes(
      state,
      buildSerpStoreReturnContentHtml(state.source, t),
    );
  };

  const buildSerpSeasonPlanSnapshot = (
    state: ApprovalFormValues['serpSeasonPlan'],
  ) => {
    if (!isSerpSeasonPlanType(apvType) || !state?.source) return null;
    return buildSerpSeasonPlanAttributes(
      state,
      buildSerpSeasonPlanContentHtml(state.source, t),
    );
  };

  const buildSerpLendingDelaySnapshot = (
    state: ApprovalFormValues['serpLendingDelay'],
  ) => {
    if (!isSerpLendingDelayType(apvType) || !state?.source) return null;
    return buildSerpLendingDelayAttributes(
      state,
      buildSerpLendingDelayContentHtml(state.source, t),
    );
  };

  const buildSerpStoreClosureSnapshot = (
    state: ApprovalFormValues['serpStoreClosure'],
  ) => {
    if (!isSerpStoreClosureType(apvType) || !state?.source) return null;
    return buildSerpStoreClosureAttributes(
      state,
      buildSerpStoreClosureContentHtml(state.source, t),
    );
  };

  const buildSerpIntermediaryReplaceSnapshot = (
    state: ApprovalFormValues['serpIntermediaryReplace'],
  ) => {
    if (!isSerpIntermediaryReplaceType(apvType) || !state?.source) return null;
    return buildSerpIntermediaryReplaceAttributes(
      state,
      buildSerpIntermediaryReplaceContentHtml(state.source, t),
    );
  };

  const buildSerpSellingPriceSnapshot = (
    state: ApprovalFormValues['serpSellingPrice'],
  ) => {
    if (!isSerpSellingPriceType(apvType) || !state?.source) return null;
    return buildSerpSellingPriceAttributes(
      state,
      buildSerpSellingPriceContentHtml(state.source, t),
    );
  };

  const buildSerpBulkStockAdjustmentSnapshot = (
    state: ApprovalFormValues['serpBulkStockAdjustment'],
  ) => {
    if (!isSerpBulkStockAdjustmentType(apvType) || !state?.source) return null;
    return buildSerpBulkStockAdjustmentAttributes(
      state,
      buildSerpBulkStockAdjustmentContentHtml(state.source, t),
    );
  };

  const buildSerpSeasonEndSnapshot = (
    state: ApprovalFormValues['serpSeasonEnd'],
  ) => {
    if (!isSerpSeasonEndType(apvType) || !state?.source) return null;
    return buildSerpSeasonEndAttributes(
      state,
      buildSerpSeasonEndContentHtml(state.source, state.detail, t),
    );
  };

  const buildSerpApStockTransferSnapshot = (
    state: ApprovalFormValues['serpApStockTransfer'],
  ) => {
    if (!isSerpApStockTransferType(apvType) || !state?.source) return null;
    return buildSerpApStockTransferAttributes(
      state,
      buildSerpApStockTransferContentHtml(state.source, t),
    );
  };

  const buildSerpStockAdjustmentSnapshot = (
    state: ApprovalFormValues['serpStockAdjustment'],
  ) => {
    if (!isSerpStockAdjustmentType(apvType) || !state?.source) return null;
    return buildSerpStockAdjustmentAttributes(
      state,
      buildSerpStockAdjustmentContentHtml(state.source, t),
    );
  };

  const buildSerpStoreChangeSnapshot = (
    state: ApprovalFormValues['serpStoreChange'],
  ) => {
    if (!isSerpStoreChangeType(apvType) || !state?.source) return null;
    return buildSerpStoreChangeAttributes(
      state,
      buildSerpStoreChangeContentHtml(state.source, t),
    );
  };

  const myInfo = useAtomValue(MyInfoState);
  const buildSalesBrandPermissionSnapshot = (
    state: ApprovalFormValues['salesBrandPermission'],
  ) => {
    const employee = toVehicleCostEmployee(myInfo);
    if (!isSalesBrandPermissionType(apvType) || !state?.brands || !employee) {
      return null;
    }
    return buildSalesBrandPermissionAttributes(
      state,
      buildSalesBrandPermissionContentHtml({
        employee,
        brands: state.brands,
        checked: state.checked,
        t,
      }),
    );
  };

  const buildSapBondStatusSnapshot = (
    state: ApprovalFormValues['sapBondStatus'],
  ) => {
    if (!isSapBondStatusType(apvType) || !state) return null;
    return buildSapBondStatusAttributes(
      state,
      buildSapBondStatusContentHtml({
        brandName: state.brandName || state.brandCode,
        period: formatSapBondStatusPeriod(state.year, state.month),
        data: state.data,
        t,
      }),
    );
  };

  const buildSaTravelExpenseSnapshot = (
    state: ApprovalFormValues['saTravelExpense'],
  ) => {
    if (!isSaTravelType(apvType) || !state) return null;
    return buildSaTravelExpenseAttributes(
      state,
      isSaTravelRequestType(apvType)
        ? buildSaTravelRequestContentHtml(state.header, t)
        : buildSaTravelExpenseContentHtml(state.header, t),
      apvType,
    );
  };

  // 작성 화면은 20행 넘으면 접어 두지만 저장은 전체 행(레거시 동일)
  const buildFncoPriceSupplySnapshot = (
    state: ApprovalFormValues['fncoPriceSupply'],
  ) => {
    if (!isFncoPriceType(apvType) || !state) return null;
    return buildFncoPriceSupplyAttributes(
      state,
      buildFncoPriceSupplyContentHtml({
        apvType,
        items: getFncoPriceSupplyItems(state.data),
        t,
      }),
    );
  };

  const buildAccountingBpSnapshot = (
    state: ApprovalFormValues['accountingBp'],
    form: ApprovalFormValues['bpRegistration'],
  ) => {
    if (!isAccountingBpType(apvType) || !state) return null;
    return buildAccountingBpAttributes(
      state,
      form,
      buildAccountingBpContentHtml(state, form, t),
      t,
    );
  };

  const buildSapCollateralChangeSnapshot = (
    state: ApprovalFormValues['sapCollateralChange'],
  ) => {
    if (!isSapCollateralType(apvType) || !state) return null;
    return buildSapCollateralChangeAttributes(
      state,
      buildSapCollateralChangeContentHtml({
        apvType,
        before: state.before,
        after: state.after,
        t,
      }),
    );
  };

  const buildInfluencerMonthlyReportSnapshot = (
    state: ApprovalFormValues['influencerMonthlyReport'],
  ) => {
    if (!isInfluencerMonthlyReportType(apvType) || !state) return null;
    return buildInfluencerMonthlyReportAttributes(
      state,
      buildInfluencerMonthlyReportContentHtml({ report: state.report, t }),
    );
  };

  const buildProductionCostSnapshot = (
    state: ApprovalFormValues['productionCost'],
    editorContent: string,
  ) => {
    if (!isProductionCostType(apvType) || !state?.data) return null;
    return buildProductionCostAttributes(
      state,
      buildProductionCostContentHtml({
        closingMonth: state.closingMonth,
        settlement: state.data.api_data,
        notes: state.notes,
        t,
        lang: i18n.language,
      }),
      editorContent,
    );
  };

  const buildInfluencerPplSnapshot = (
    state: ApprovalFormValues['influencerPpl'],
  ) => {
    if (!isInfluencerPplType(apvType) || !state?.report) return null;
    return buildInfluencerPplAttributes(
      state,
      buildInfluencerPplContentHtml({
        report: state.report,
        purpose: state.purpose,
        t,
      }),
    );
  };

  const buildSapBudgetIncreaseSnapshot = (
    state: ApprovalFormValues['sapBudgetIncrease'],
  ) => {
    if (!isSapBudgetIncreaseType(apvType) || !state) return null;
    return buildSapBudgetIncreaseAttributes(
      state,
      buildSapBudgetIncreaseContentHtml({
        department: formatBudgetDepartment(myInfo),
        yearMonth: state.yearMonth,
        account: findSelectedAccount(state),
        amount: state.amount,
        t,
      }),
    );
  };

  const buildSnapshot = () => {
    const v = getValues();
    return {
      apvType,
      subject: v.subject,
      content: v.content,
      secuLevel: v.secuLevel,
      restrictReferrer: v.restrictReferrer,
      approvalLine,
      referrers,
      attachments: v.attachments,
      showImagesBelowContent: v.showImagesBelowContent,
      contractIds: v.contractIds,
      contractContent: v.contractContent,
      cancelTarget: v.cancelTarget,
      saVoucher: v.saVoucher,
      expensePreApproval: v.expensePreApproval,
      openConstructionCost: v.openConstructionCost
        ? {
            ...v.openConstructionCost,
            content: (isOpenConstructionCostSettlementType(apvType)
              ? buildOpenConstructionCostSettlementContentHtml
              : buildOpenConstructionCostContentHtml)(
              v.openConstructionCost.data,
            ),
          }
        : null,
      fncoPreMarketing: v.fncoPreMarketing
        ? buildFncoPreMarketingAttributes(
            v.fncoPreMarketing.analysisId,
            buildFncoPreMarketingContentHtml(v.fncoPreMarketing.analysis, t),
          )
        : null,
      marketingExpense: v.marketingExpense
        ? buildMarketingExpenseAttributes(
            v.marketingExpense.analysisId,
            buildMarketingExpenseContentHtml(v.marketingExpense.analysis, t),
          )
        : null,
      entExpense: v.entExpense,
      workSpecChecklist: isWorkSpecificationType(apvType)
        ? v.workSpecChecklist
        : null,
      fncoShowRoomRequestNumber: v.fncoShowRoomRequestNumber,
      fncoPurchaseRequestNumber: v.fncoPurchaseRequestNumber,
      vehicleCostContent: v.vehicleCostContent,
      resignation: buildResignationSnapshot(v.resignation),
      longServiceLeave: buildLongServiceLeaveSnapshot(v.longServiceLeave),
      newCorporateCard: buildNewCorporateCardSnapshot(v.newCorporateCard),
      bpRegistration: buildBpSnapshot(v.bpRegistration),
      corporateSeal: v.corporateSeal,
      shopManagerFee: v.shopManagerFee,
      sapContractFee: buildSapContractFeeSnapshot(v.sapContractFee),
      serpStockAdjustment: buildSerpStockAdjustmentSnapshot(
        v.serpStockAdjustment,
      ),
      serpStoreChange: buildSerpStoreChangeSnapshot(v.serpStoreChange),
      serpStoreOpen: buildSerpStoreOpenSnapshot(v.serpStoreOpen),
      serpStoreReturn: buildSerpStoreReturnSnapshot(v.serpStoreReturn),
      serpLendingDelay: buildSerpLendingDelaySnapshot(v.serpLendingDelay),
      serpStoreClosure: buildSerpStoreClosureSnapshot(v.serpStoreClosure),
      serpIntermediaryReplace: buildSerpIntermediaryReplaceSnapshot(
        v.serpIntermediaryReplace,
      ),
      serpSellingPrice: buildSerpSellingPriceSnapshot(v.serpSellingPrice),
      serpSeasonPlan: buildSerpSeasonPlanSnapshot(v.serpSeasonPlan),
      serpBulkStockAdjustment: buildSerpBulkStockAdjustmentSnapshot(
        v.serpBulkStockAdjustment,
      ),
      serpSeasonEnd: buildSerpSeasonEndSnapshot(v.serpSeasonEnd),
      serpApStockTransfer: buildSerpApStockTransferSnapshot(
        v.serpApStockTransfer,
      ),
      mortgageFee: v.mortgageFee,
      storeOpen: isStoreOpenType(apvType) ? v.storeOpen : null,
      corporateCardRequest: v.corporateCardRequest,
      annualLeaveRequest: v.annualLeaveRequest,
      probationEvaluation: v.probationEvaluation,
      salesBrandPermission: buildSalesBrandPermissionSnapshot(
        v.salesBrandPermission,
      ),
      groupSales: isGroupSalesType(apvType) ? v.groupSales : null,
      groupSalesMulti: isGroupSalesMultiType(apvType)
        ? v.groupSalesMulti
        : null,
      marketingCase: isMarketingCaseType(apvType) ? v.marketingCase : null,
      productionVendor: isProductionVendorType(apvType)
        ? buildProductionVendorAttributes(v.productionVendor)
        : null,
      productLifecycle: isProductLifecycleType(apvType)
        ? buildProductLifecycleAttributes(v.productLifecycle)
        : null,
      styleMasterPrice: isStyleMasterPriceType(apvType)
        ? buildStyleMasterPriceAttributes(v.styleMasterPrice)
        : null,
      productionCostClosing: isProductionCostClosingType(apvType)
        ? buildProductionCostClosingAttributes(v.productionCostClosing)
        : null,
      claimClosing: isClaimClosingType(apvType)
        ? buildClaimClosingAttributes(v.claimClosing)
        : null,
      inventoryReplenishment: isInventoryReplenishmentType(apvType)
        ? buildInventoryReplenishmentAttributes(v.inventoryReplenishment)
        : null,
      storageMaterialOrder: isStorageMaterialOrderType(apvType)
        ? buildStorageMaterialOrderAttributes(v.storageMaterialOrder)
        : null,
      supplierEvaluation: isSupplierEvaluationType(apvType)
        ? buildSupplierEvaluationAttributes(v.supplierEvaluation)
        : null,
      purchaseRequisition: isPurchaseRequisitionType(apvType)
        ? buildPurchaseRequisitionAttributes(v.purchaseRequisition)
        : null,
      costQuotation: isCostQuotationType(apvType)
        ? buildCostQuotationAttributes(v.costQuotation)
        : null,
      salesContract: isSalesContractType(apvType)
        ? buildSalesContractAttributes(v.salesContract)
        : null,
      creditNote: isCreditNoteType(apvType)
        ? buildCreditNoteAttributes(v.creditNote)
        : null,
      claimNote: isClaimNoteType(apvType)
        ? buildClaimNoteAttributes(v.claimNote)
        : null,
      sapBudgetIncrease: buildSapBudgetIncreaseSnapshot(v.sapBudgetIncrease),
      sapCollateralChange: buildSapCollateralChangeSnapshot(
        v.sapCollateralChange,
      ),
      accountingBp: buildAccountingBpSnapshot(v.accountingBp, v.bpRegistration),
      influencerMonthlyReport: buildInfluencerMonthlyReportSnapshot(
        v.influencerMonthlyReport,
      ),
      productionCost: buildProductionCostSnapshot(
        v.productionCost,
        v.content ?? '',
      ),
      externalLoan: isExternalLoanType(apvType)
        ? buildExternalLoanAttributes(v.externalLoan)
        : null,
      sapBondStatus: buildSapBondStatusSnapshot(v.sapBondStatus),
      saTravelExpense: buildSaTravelExpenseSnapshot(v.saTravelExpense),
      fncoPriceSupply: buildFncoPriceSupplySnapshot(v.fncoPriceSupply),
      discountVoucher: v.discountVoucher,
      expenseVoucher: v.expenseVoucher,
      influencerPpl: buildInfluencerPplSnapshot(v.influencerPpl),
    };
  };

  // 첫 POST 성공 시 docId 를 폼/부모에 동기화 — 이후 저장은 PUT. 반환값은 권위 있는 documentId.
  const dispatchSave = async (status: 'DRAFT' | 'SUBMIT'): Promise<string> => {
    // payload 와 같은 시점에 가드 baseline 캡처 — 요청 중 편집분이 저장된 것으로 오인되지 않게.
    const commitSavedBaseline = captureSaveBaseline();
    const payload = approvalFormToCreatePayload(buildSnapshot(), status);
    if (documentId) {
      await update(documentId, payload);
      commitSavedBaseline();
      await invalidateDocumentCache(documentId);
      return documentId;
    }
    const response = await create(payload);
    commitSavedBaseline();
    const formDocId = getValues('docId');
    const persistedDocId = extractResponseDocumentId(response) ?? formDocId;
    if (persistedDocId !== formDocId) {
      setValue('docId', persistedDocId);
    }
    await invalidateDocumentCache(persistedDocId);
    onPersisted?.(persistedDocId);
    return persistedDocId;
  };

  // 본 저장 성공 후 연관품의 동기화 — 동기화 자체 실패는 본 저장에 영향 없음, 토스트만.
  const syncRelationsAfterSave = async (persistedDocId: string) => {
    try {
      await syncRelations(persistedDocId);
    } catch (e) {
      snackbar.showError({
        message: messageForApiError(
          e,
          t('연관품의 동기화에 실패했습니다.'),
          false,
          t,
        ),
      });
    } finally {
      // 연관품의는 단건 응답 embedded 로 읽으므로 동기화 후 재무효화 — 모달 유지 복귀라
      // 조회 화면이 unmount 되지 않아 이 무효화가 유일한 갱신 기회. 동기화는 delete→add
      // 순차 실행이라 부분 성공이 가능해 실패 시에도 서버 상태를 다시 읽어야 한다.
      await invalidateDocumentCache(persistedDocId);
    }
  };

  // 임시저장도 서버가 빈 제목·결재선 최소 인원을 422 로 막으므로 상신과 같은 기준으로 사전 차단 —
  // 서버 왕복 없이 동일 문구 노출. 그 외 항목은 무검증이라 422 등은 서버 메시지로 토스트.
  const handleDraft = async () => {
    if (buttonsLocked) return;
    const subjectMessage = checkSubjectRequired(getValues('subject') ?? '');
    if (subjectMessage) {
      snackbar.showError({ message: t(subjectMessage) });
      return;
    }
    const lineShortageMessage = checkApprovalLineShortage(lineMemberIds);
    if (lineShortageMessage) {
      snackbar.showError({ message: t(lineShortageMessage) });
      return;
    }
    // 사전 비용(205)은 레거시부터 임시저장도 견적·지급 총액 검사를 거침(서버도 attributes 필수)
    if (isExpensePreApprovalType(apvType)) {
      const expenseMessage = checkExpenseBeforeSave(
        getValues('expensePreApproval'),
      );
      if (expenseMessage) {
        snackbar.showError({ message: t(expenseMessage) });
        return;
      }
    }
    // 생산비 마감(216)은 레거시부터 임시저장도 마감 월·조회 결과 필수(서버도 data 필수)
    if (isProductionCostType(apvType)) {
      const productionCostMessage = checkProductionCostBeforeSave(
        getValues('productionCost'),
      );
      if (productionCostMessage) {
        snackbar.showError({ message: t(productionCostMessage) });
        return;
      }
    }
    // 외담대 발행(217)은 서버가 임시저장에도 발행내역 1건 이상·결재묶음번호를 요구
    if (isExternalLoanType(apvType)) {
      const externalLoanMessage = checkExternalLoanBeforeSave(
        getValues('externalLoan'),
      );
      if (externalLoanMessage) {
        snackbar.showError({ message: t(externalLoanMessage) });
        return;
      }
    }
    // 매장 OPEN(233)도 레거시부터 임시저장에 분석 결과 필수(서버도 data 비면 422)
    if (isOpenConstructionCostType(apvType)) {
      const analysisMessage = checkOpenConstructionCostBeforeSave(
        getValues('openConstructionCost'),
      );
      if (analysisMessage) {
        snackbar.showError({ message: t(analysisMessage) });
        return;
      }
    }
    // SERP 재고실사 조정(169) — 레거시부터 임시저장도 SERP 데이터 필수
    if (isSerpStockAdjustmentType(apvType)) {
      const serpMessage = checkSerpStockAdjustmentBeforeSave(
        getValues('serpStockAdjustment'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    // SERP 매장 변경(162) — 레거시부터 임시저장도 SERP 데이터 필수
    if (isSerpStoreChangeType(apvType)) {
      const serpMessage = checkSerpStoreChangeBeforeSave(
        getValues('serpStoreChange'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    if (isSerpStoreOpenType(apvType)) {
      const serpMessage = checkSerpStoreOpenBeforeSave(
        getValues('serpStoreOpen'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    if (isSerpStoreReturnType(apvType)) {
      const serpMessage = checkSerpStoreReturnBeforeSave(
        getValues('serpStoreReturn'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    if (isSerpSeasonPlanType(apvType)) {
      const serpMessage = checkSerpSeasonPlanBeforeSave(
        getValues('serpSeasonPlan'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    if (isSerpLendingDelayType(apvType)) {
      const serpMessage = checkSerpLendingDelayBeforeSave(
        getValues('serpLendingDelay'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    if (isSerpStoreClosureType(apvType)) {
      const serpMessage = checkSerpStoreClosureBeforeSave(
        getValues('serpStoreClosure'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    if (isSerpIntermediaryReplaceType(apvType)) {
      const serpMessage = checkSerpIntermediaryReplaceBeforeSave(
        getValues('serpIntermediaryReplace'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    if (isSerpSellingPriceType(apvType)) {
      const serpMessage = checkSerpSellingPriceBeforeSave(
        getValues('serpSellingPrice'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    if (isSerpBulkStockAdjustmentType(apvType)) {
      const serpMessage = checkSerpBulkStockAdjustmentBeforeSave(
        getValues('serpBulkStockAdjustment'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    if (isSerpSeasonEndType(apvType)) {
      const serpMessage = checkSerpSeasonEndBeforeSave(
        getValues('serpSeasonEnd'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    if (isSerpApStockTransferType(apvType)) {
      const serpMessage = checkSerpApStockTransferBeforeSave(
        getValues('serpApStockTransfer'),
      );
      if (serpMessage) {
        snackbar.showError({ message: t(serpMessage) });
        return;
      }
    }
    // F&CO 마케팅 사전(221) — 서버가 analysisId·content 필수
    if (isFncoPreMarketingType(apvType)) {
      const fncoMessage = checkFncoPreMarketingBeforeSave(
        getValues('fncoPreMarketing'),
      );
      if (fncoMessage) {
        snackbar.showError({ message: t(fncoMessage) });
        return;
      }
    }
    // 마케팅 비용(212) — 서버가 analysisId·content 필수
    if (isMarketingExpenseType(apvType)) {
      const marketingMessage = checkMarketingExpenseBeforeSave(
        getValues('marketingExpense'),
      );
      if (marketingMessage) {
        snackbar.showError({ message: t(marketingMessage) });
        return;
      }
    }
    // 단체판매 진행(61)은 레거시부터 임시저장도 건 선택 필수
    if (isGroupSalesType(apvType)) {
      const groupSalesMessage = checkGroupSalesBeforeSave(
        getValues('groupSales'),
      );
      if (groupSalesMessage) {
        snackbar.showError({ message: t(groupSalesMessage) });
        return;
      }
    }
    // (구)마케팅 건별 진행(83)은 레거시부터 임시저장도 「확인」 필수
    if (isMarketingCaseType(apvType)) {
      const marketingCaseMessage = checkMarketingCaseBeforeSave(
        getValues('marketingCase'),
      );
      if (marketingCaseMessage) {
        snackbar.showError({ message: t(marketingCaseMessage) });
        return;
      }
    }
    // GERP 저장부자재 발주(125)도 레거시부터 임시저장에 「확인」 필수
    if (isStorageMaterialOrderType(apvType)) {
      const storageMaterialOrderMessage = checkStorageMaterialOrderBeforeSave(
        getValues('storageMaterialOrder'),
      );
      if (storageMaterialOrderMessage) {
        snackbar.showError({ message: t(storageMaterialOrderMessage) });
        return;
      }
    }
    // GERP 협력사 평가(127)도 레거시부터 임시저장에 「확인」 필수
    if (isSupplierEvaluationType(apvType)) {
      const supplierEvaluationMessage = checkSupplierEvaluationBeforeSave(
        getValues('supplierEvaluation'),
      );
      if (supplierEvaluationMessage) {
        snackbar.showError({ message: t(supplierEvaluationMessage) });
        return;
      }
    }
    // GERP 생산비 마감(124)도 레거시부터 임시저장에 「확인」 필수
    if (isProductionCostClosingType(apvType)) {
      const productionCostClosingMessage = checkProductionCostClosingBeforeSave(
        getValues('productionCostClosing'),
      );
      if (productionCostClosingMessage) {
        snackbar.showError({ message: t(productionCostClosingMessage) });
        return;
      }
    }
    // GERP CLAIM NOTE(166)도 레거시부터 임시저장에 카드 필수
    if (isClaimNoteType(apvType)) {
      const claimNoteMessage = checkClaimNoteBeforeSave(getValues('claimNote'));
      if (claimNoteMessage) {
        snackbar.showError({ message: t(claimNoteMessage) });
        return;
      }
    }
    // GERP 클레임 마감(186)도 레거시부터 임시저장에 카드 필수
    if (isClaimClosingType(apvType)) {
      const claimClosingMessage = checkClaimClosingBeforeSave(
        getValues('claimClosing'),
      );
      if (claimClosingMessage) {
        snackbar.showError({ message: t(claimClosingMessage) });
        return;
      }
    }
    // GERP PR(제상품)(122)도 레거시부터 임시저장에 카드 필수
    if (isPurchaseRequisitionType(apvType)) {
      const purchaseRequisitionMessage = checkPurchaseRequisitionBeforeSave(
        getValues('purchaseRequisition'),
      );
      if (purchaseRequisitionMessage) {
        snackbar.showError({ message: t(purchaseRequisitionMessage) });
        return;
      }
    }
    // GERP 원가 견적서(121)도 레거시부터 임시저장에 카드 필수
    if (isCostQuotationType(apvType)) {
      const costQuotationMessage = checkCostQuotationBeforeSave(
        getValues('costQuotation'),
      );
      if (costQuotationMessage) {
        snackbar.showError({ message: t(costQuotationMessage) });
        return;
      }
    }
    // 단체판매 진행(다중)(131)도 레거시부터 임시저장에 건 선택 필수
    if (isGroupSalesMultiType(apvType)) {
      const groupSalesMultiMessage = checkGroupSalesMultiBeforeSave(
        getValues('groupSalesMulti'),
      );
      if (groupSalesMultiMessage) {
        snackbar.showError({ message: t(groupSalesMultiMessage) });
        return;
      }
    }
    // 쇼룸 출고(209)는 레거시부터 임시저장도 요청번호 필수(서버도 422)
    if (
      isFncoShowRoomType(apvType) &&
      !getValues('fncoShowRoomRequestNumber')
    ) {
      snackbar.showError({ message: t(FNCO_SHOW_ROOM_MISSING_MESSAGE) });
      return;
    }
    // 신제품 발주(202)·운영제품 재발주(203)도 임시저장부터 요청번호 필수(서버 422)
    if (
      isFncoProductPurchaseType(apvType) &&
      !getValues('fncoPurchaseRequestNumber')
    ) {
      snackbar.showError({
        message: t(FNCO_PRODUCT_PURCHASE_MISSING_MESSAGE),
      });
      return;
    }
    // 가격 등록 공급가·할인율·판매가 요청(197~199) — 임시저장도 요청번호 필수(서버 422), 조회 중엔 빈 표로 저장되지 않게
    if (isFncoPriceType(apvType)) {
      const priceMessage = checkFncoPriceSupplyBeforeSave(
        getValues('fncoPriceSupply'),
      );
      if (priceMessage) {
        snackbar.showError({ message: t(priceMessage) });
        return;
      }
    }
    // 차량유지비(93) — 서버가 content 필수
    if (isVehicleCostType(apvType)) {
      const vehicleMessage = checkVehicleCostBeforeSave(
        getValues('vehicleCostContent'),
      );
      if (vehicleMessage) {
        snackbar.showError({ message: t(vehicleMessage) });
        return;
      }
    }
    // 퇴직원(196) — 레거시부터 임시저장도 상신과 같은 검사
    if (isResignationType(apvType)) {
      const resignationMessage = checkResignationBeforeSave(
        getValues('resignation'),
      );
      if (resignationMessage) {
        snackbar.showError({ message: t(resignationMessage) });
        return;
      }
    }
    // 신규입사자 법인카드(204) — 레거시부터 임시저장도 상신과 같은 검사
    if (isNewCorporateCardType(apvType)) {
      const newCardMessage = checkNewCorporateCardBeforeSave(
        getValues('newCorporateCard'),
      );
      if (newCardMessage) {
        snackbar.showError({ message: t(newCardMessage) });
        return;
      }
    }
    // 거래처 신규/변경 HK(200)·TW(201) — 레거시부터 임시저장도 상신과 같은 검사
    if (isBpRegistrationType(apvType)) {
      const bpMessage = checkBpBeforeSave(getValues('bpRegistration'));
      if (bpMessage) {
        snackbar.showError({ message: t(bpMessage) });
        return;
      }
    }
    // (구)근저당설정비용 지급(81) — 레거시부터 임시저장도 상신과 같은 검사
    if (isMortgageFeeType(apvType)) {
      const mortgageMessage = checkMortgageFeeBeforeSave(
        getValues('mortgageFee'),
      );
      if (mortgageMessage) {
        snackbar.showError({ message: t(mortgageMessage) });
        return;
      }
    }
    // (구)매장 OPEN(8) — 백화점·직영점 양식은 매장을 「등록」해야 임시저장·상신 가능
    if (isStoreOpenType(apvType)) {
      const storeOpenMessage = checkStoreOpenBeforeSave(getValues('storeOpen'));
      if (storeOpenMessage) {
        snackbar.showError({ message: t(storeOpenMessage) });
        return;
      }
    }
    // 법인카드 한도증액·재발급(206·207) — 레거시부터 임시저장도 상신과 같은 검사
    if (isCorporateCardRequestType(apvType)) {
      const cardRequestMessage = checkCorporateCardRequestBeforeSave(
        apvType,
        getValues('corporateCardRequest'),
      );
      if (cardRequestMessage) {
        snackbar.showError({ message: t(cardRequestMessage) });
        return;
      }
    }
    // 연차휴가(32) — 레거시부터 임시저장도 상신과 같은 검사
    if (isAnnualLeaveRequestType(apvType)) {
      const leaveMessage = checkAnnualLeaveRequestBeforeSave(
        getValues('annualLeaveRequest'),
      );
      if (leaveMessage) {
        snackbar.showError({ message: t(leaveMessage) });
        return;
      }
    }
    // F&CO 장기근속 포상휴가(240) — 레거시부터 임시저장도 서약 동의 필수
    if (isLongServiceLeaveType(apvType)) {
      const current = getValues('longServiceLeave');
      const longServiceMessage = checkLongServiceLeaveBeforeSave(current);
      if (longServiceMessage) {
        snackbar.showError({ message: t(longServiceMessage) });
        if (isPledgeMissing(longServiceMessage)) focusLongServicePledge();
        return;
      }
    }
    // 수습평가서(188) — 레거시부터 임시저장도 대상자 확인 필수
    if (isProbationEvaluationType(apvType)) {
      const probationMessage = checkProbationEvaluationBeforeSave(
        getValues('probationEvaluation'),
      );
      if (probationMessage) {
        snackbar.showError({ message: t(probationMessage) });
        return;
      }
    }
    // Sales 브랜드 권한 변경(215) — 레거시부터 변경분 없으면 임시저장도 막음
    if (isSalesBrandPermissionType(apvType)) {
      const salesBrandMessage = checkSalesBrandPermissionBeforeSave(
        getValues('salesBrandPermission'),
      );
      if (salesBrandMessage) {
        snackbar.showError({ message: t(salesBrandMessage) });
        return;
      }
    }
    // 할인권 발행(68)은 레거시부터 임시저장도 할인권 필수
    if (isDiscountVoucherType(apvType)) {
      const voucherMessage = checkDiscountVoucherBeforeSave(
        getValues('discountVoucher'),
      );
      if (voucherMessage) {
        snackbar.showError({ message: t(voucherMessage) });
        return;
      }
    }
    if (isEntExpenseType(apvType)) {
      const entViolation = findEntExpenseViolation(getValues('entExpense'));
      if (entViolation) {
        snackbar.showError({ message: t(entViolation.message) });
        focusEntExpenseViolation(entViolation);
        return;
      }
    }
    if (isWorkSpecificationType(apvType)) {
      const checklistMessage = checkWorkSpecificationBeforeSave(
        getValues('workSpecChecklist'),
      );
      if (checklistMessage) {
        snackbar.showError({ message: t(checklistMessage) });
        return;
      }
    }
    // isSubmitting만으론 저장 완료~연관품의 동기화 사이에 잠금이 풀림 — 동기화까지 유지.
    setIsDraftFlowLocked(true);
    try {
      let persistedDocId: string;
      try {
        persistedDocId = await dispatchSave('DRAFT');
      } catch (e) {
        snackbar.showError({
          message: messageForApiError(
            e,
            t('임시저장에 실패했습니다.'),
            isEditMode,
            t,
          ),
        });
        return;
      }
      await syncRelationsAfterSave(persistedDocId);
      void requestSummary(persistedDocId);
      snackbar.showSuccess({ message: t('임시저장이 완료되었습니다.') });
    } finally {
      setIsDraftFlowLocked(false);
    }
  };

  // 고정 결재선이 결재선 최소 인원을 채우는 양식은 진입 상태 그대로(기본 제목·빈 본문)도
  // 모든 검사를 통과한다. 취소 품의는 원 품의만으로 완결이라 본문이 비는 게 정상.
  const shouldConfirmEmptySubmit = () =>
    !isEditMode &&
    !CANCEL_APPROVAL_TYPES.includes(apvType) &&
    !shouldWarnOnExit() &&
    isEmptyContent(getValues('content') ?? '');

  /** 결재 진행 시작(SUBMIT). 성공 시 연관품의 동기화 → 조회 이동(또는 앱 화면 닫기). */
  const handleSubmitFlow = async (emptySubmitConfirmed = false) => {
    if (buttonsLocked) return;
    setIsSubmitFlowLocked(true);

    // 퇴사자 판정 응답 전 상신하면 차단이 빠져나감 — 판정 근거 도착까지 보류
    if (
      isDeactivatedCheckLoading ||
      !lineActivationResolved ||
      !referrerActivationResolved
    ) {
      snackbar.showError({
        message: t(
          '결재라인 정보를 확인하는 중입니다. 잠시 후 다시 시도해주세요.',
        ),
      });
      releaseSubmitLockAfterMin();
      return;
    }

    // live 판정 조회가 실패한 상태도 근거 없음은 마찬가지라 상신을 보류한다.
    // 문서 단위 조회(useDeactivatedApprovers) error 는 일부러 안 본다 —
    // key 가 documentId 고정이라 실패가 안 풀려 상신이 영구 차단되고,
    // 현재 라인 전원은 live 판정이 커버해 근거가 이미 있다.
    if (lineActivationError || referrerActivationError) {
      snackbar.showError({
        message: t(
          '결재라인 정보를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.',
        ),
      });
      releaseSubmitLockAfterMin();
      return;
    }

    // 소속 판정이 퇴사 판정 응답을 쓰므로 위 보류 가드 뒤에 둠
    const v = getValues();
    const violation = findSubmitViolation({
      apvType,
      subject: v.subject ?? '',
      lineMemberIds,
      contractIds: v.contractIds ?? [],
      cancelTarget: v.cancelTarget,
      sapDocumentNo: v.saVoucher?.vouchers.length
        ? v.saVoucher.sapDocumentNo
        : null,
      coApproverIds,
      memberOrganizations,
      expensePreApproval: isExpensePreApprovalType(apvType)
        ? v.expensePreApproval
        : undefined,
      openConstructionCost: isOpenConstructionCostType(apvType)
        ? v.openConstructionCost
        : undefined,
      fncoPreMarketing: isFncoPreMarketingType(apvType)
        ? v.fncoPreMarketing
        : undefined,
      marketingExpense: isMarketingExpenseType(apvType)
        ? v.marketingExpense
        : undefined,
      entExpense: isEntExpenseType(apvType) ? v.entExpense : undefined,
      workSpecChecklist: isWorkSpecificationType(apvType)
        ? v.workSpecChecklist
        : undefined,
      fncoShowRoomRequestNumber: isFncoShowRoomType(apvType)
        ? v.fncoShowRoomRequestNumber
        : undefined,
      fncoPurchaseRequestNumber: isFncoProductPurchaseType(apvType)
        ? v.fncoPurchaseRequestNumber
        : undefined,
      vehicleCostContent: isVehicleCostType(apvType)
        ? v.vehicleCostContent
        : undefined,
      resignation: isResignationType(apvType) ? v.resignation : undefined,
      longServiceLeave: isLongServiceLeaveType(apvType)
        ? v.longServiceLeave
        : undefined,
      newCorporateCard: isNewCorporateCardType(apvType)
        ? v.newCorporateCard
        : undefined,
      bpRegistration:
        isBpRegistrationType(apvType) || isAccountingBpType(apvType)
          ? v.bpRegistration
          : undefined,
      shopManagerFee: isShopManagerFeeType(apvType)
        ? v.shopManagerFee
        : undefined,
      sapContractFee: isSapContractFeeType(apvType)
        ? v.sapContractFee
        : undefined,
      serpStockAdjustment: isSerpStockAdjustmentType(apvType)
        ? v.serpStockAdjustment
        : undefined,
      serpStoreChange: isSerpStoreChangeType(apvType)
        ? v.serpStoreChange
        : undefined,
      serpStoreOpen: isSerpStoreOpenType(apvType) ? v.serpStoreOpen : undefined,
      serpStoreReturn: isSerpStoreReturnType(apvType)
        ? v.serpStoreReturn
        : undefined,
      serpLendingDelay: isSerpLendingDelayType(apvType)
        ? v.serpLendingDelay
        : undefined,
      serpStoreClosure: isSerpStoreClosureType(apvType)
        ? v.serpStoreClosure
        : undefined,
      serpIntermediaryReplace: isSerpIntermediaryReplaceType(apvType)
        ? v.serpIntermediaryReplace
        : undefined,
      serpSellingPrice: isSerpSellingPriceType(apvType)
        ? v.serpSellingPrice
        : undefined,
      serpSeasonPlan: isSerpSeasonPlanType(apvType)
        ? v.serpSeasonPlan
        : undefined,
      serpBulkStockAdjustment: isSerpBulkStockAdjustmentType(apvType)
        ? v.serpBulkStockAdjustment
        : undefined,
      serpSeasonEnd: isSerpSeasonEndType(apvType) ? v.serpSeasonEnd : undefined,
      serpApStockTransfer: isSerpApStockTransferType(apvType)
        ? v.serpApStockTransfer
        : undefined,
      mortgageFee: isMortgageFeeType(apvType) ? v.mortgageFee : undefined,
      storeOpen: isStoreOpenType(apvType) ? v.storeOpen : undefined,
      corporateCardRequest: isCorporateCardRequestType(apvType)
        ? v.corporateCardRequest
        : undefined,
      annualLeaveRequest: isAnnualLeaveRequestType(apvType)
        ? v.annualLeaveRequest
        : undefined,
      probationEvaluation: isProbationEvaluationType(apvType)
        ? v.probationEvaluation
        : undefined,
      salesBrandPermission: isSalesBrandPermissionType(apvType)
        ? v.salesBrandPermission
        : undefined,
      groupSales: isGroupSalesType(apvType) ? v.groupSales : undefined,
      groupSalesMulti: isGroupSalesMultiType(apvType)
        ? v.groupSalesMulti
        : undefined,
      marketingCase: isMarketingCaseType(apvType) ? v.marketingCase : undefined,
      productionVendor: isProductionVendorType(apvType)
        ? v.productionVendor
        : undefined,
      productLifecycle: isProductLifecycleType(apvType)
        ? v.productLifecycle
        : undefined,
      styleMasterPrice: isStyleMasterPriceType(apvType)
        ? v.styleMasterPrice
        : undefined,
      productionCostClosing: isProductionCostClosingType(apvType)
        ? v.productionCostClosing
        : undefined,
      claimClosing: isClaimClosingType(apvType) ? v.claimClosing : undefined,
      inventoryReplenishment: isInventoryReplenishmentType(apvType)
        ? v.inventoryReplenishment
        : undefined,
      storageMaterialOrder: isStorageMaterialOrderType(apvType)
        ? v.storageMaterialOrder
        : undefined,
      supplierEvaluation: isSupplierEvaluationType(apvType)
        ? v.supplierEvaluation
        : undefined,
      purchaseRequisition: isPurchaseRequisitionType(apvType)
        ? v.purchaseRequisition
        : undefined,
      costQuotation: isCostQuotationType(apvType) ? v.costQuotation : undefined,
      salesContract: isSalesContractType(apvType) ? v.salesContract : undefined,
      creditNote: isCreditNoteType(apvType) ? v.creditNote : undefined,
      claimNote: isClaimNoteType(apvType) ? v.claimNote : undefined,
      sapBudgetIncrease: isSapBudgetIncreaseType(apvType)
        ? v.sapBudgetIncrease
        : undefined,
      sapCollateralChange: isSapCollateralType(apvType)
        ? v.sapCollateralChange
        : undefined,
      accountingBp: isAccountingBpType(apvType) ? v.accountingBp : undefined,
      influencerMonthlyReport: isInfluencerMonthlyReportType(apvType)
        ? v.influencerMonthlyReport
        : undefined,
      productionCost: isProductionCostType(apvType)
        ? v.productionCost
        : undefined,
      externalLoan: isExternalLoanType(apvType) ? v.externalLoan : undefined,
      sapBondStatus: isSapBondStatusType(apvType) ? v.sapBondStatus : undefined,
      saTravelExpense: isSaTravelType(apvType) ? v.saTravelExpense : undefined,
      fncoPriceSupply: isFncoPriceType(apvType) ? v.fncoPriceSupply : undefined,
      discountVoucher: isDiscountVoucherType(apvType)
        ? v.discountVoucher
        : undefined,
      expenseVoucher: isExpenseVoucherType(apvType)
        ? v.expenseVoucher
        : undefined,
      influencerPpl: isInfluencerPplType(apvType) ? v.influencerPpl : undefined,
    });
    if (violation) {
      snackbar.showError({ message: t(violation) });
      if (isLongServiceLeaveType(apvType) && isPledgeMissing(violation)) {
        focusLongServicePledge();
      }
      if (isEntExpenseType(apvType)) {
        const entViolation = findEntExpenseViolation(v.entExpense);
        if (entViolation?.message === violation) {
          focusEntExpenseViolation(entViolation);
        }
      }
      if (isExpenseVoucherType(apvType)) {
        const voucherViolation = findExpenseVoucherViolation(v.expenseVoucher);
        if (voucherViolation?.message === violation) {
          setExpenseVoucherInvalid(voucherViolation.fields);
          focusExpenseVoucherField(voucherViolation.fields[0]);
        }
      }
      releaseSubmitLockAfterMin();
      return;
    }

    // 상신만 차단, 임시저장은 허용 (작성 내용 보존)
    if (deactivatedNames.length > 0) {
      snackbar.showError({
        message: t(
          '퇴사자가 포함된 결재라인은 상신할 수 없습니다. 결재자를 변경해 주세요.',
        ),
      });
      releaseSubmitLockAfterMin();
      return;
    }

    if (referrers.some((r) => deactivatedReferrerIds.has(r.id))) {
      snackbar.showError({
        message: t(
          '퇴사자가 포함된 참조인은 상신할 수 없습니다. 참조인을 변경해 주세요.',
        ),
      });
      releaseSubmitLockAfterMin();
      return;
    }

    // 바깥 클릭으로 닫으면 onCancel 이 불리지 않아 잠금을 먼저 풀어둔다
    if (!emptySubmitConfirmed && shouldConfirmEmptySubmit()) {
      setIsSubmitFlowLocked(false);
      dialog.show({
        message: t('본문이 비어 있어요.\n내용 없이 상신하시겠어요?'),
        confirmButtonText: t('상신'),
        cancelButtonText: t('취소'),
        onConfirm: () => handleSubmitFlow(true),
      });
      return;
    }

    let persistedDocId: string;
    try {
      persistedDocId = await dispatchSave('SUBMIT');
    } catch (e) {
      snackbar.showError({
        message: messageForApiError(
          e,
          t('품의 상신에 실패했습니다.'),
          isEditMode,
          t,
        ),
      });
      releaseSubmitLockAfterMin();
      return;
    }

    await syncRelationsAfterSave(persistedDocId);
    const summaryRequest = requestSummary(persistedDocId);

    // 화면 전환 뒤 상신 잠금 해제는 모든 분기에서 여기서만 — 전환이 안 되고 화면이 남으면(호스트가 닫기 취소,
    // 구버전 앱이 BACK 무시, 라우팅 실패) 버튼이 영영 잠긴다.
    const finishSubmit = async (move: () => unknown) => {
      try {
        await move();
      } finally {
        releaseSubmitLockAfterMin();
      }
    };

    // 호스트(모달)가 in-place 스왑을 직접 처리하는 경우 — 페이지/앱 분기 건너뜀.
    if (onSubmitSuccess) {
      await finishSubmit(() => {
        snackbar.showSuccess({ message: t('품의 상신이 완료되었습니다.') });
        onSubmitSuccess(persistedDocId);
      });
      return;
    }

    // 앱 웹뷰는 화면 스택을 앱이 소유 — 라우팅 대신 BACK 위임하고 토스트는 앱이 노출.
    if (isRnWebView()) {
      await waitSummaryRequest(summaryRequest);
      await finishSubmit(() =>
        sendToRN(RnMessage.BACK, {
          toast: { message: t('품의 상신이 완료되었습니다.') },
        }),
      );
      return;
    }

    // 외부 시스템이 window.open·iframe 으로 연 창도 여기로 온다 — 그 창의 부모는 닫기 메시지를 받지 못함
    await finishSubmit(() => {
      snackbar.showSuccess({ message: t('품의 상신이 완료되었습니다.') });
      return router.push(buildViewPath(persistedDocId));
    });
  };

  // edit 모드: 작성취소 시 조회 페이지로 복귀. create 모드: 목록.
  const exitDestination = () => {
    if (isEditMode && documentId) return buildViewPath(documentId);
    return APPROVAL_LIST_PATH;
  };

  const exitToList = () => {
    // 호스트(모달)가 종료 처리를 직접 담당하는 경우 — 페이지/앱 분기 건너뜀.
    if (onExit) {
      onExit();
      return;
    }
    if (isRnWebView()) {
      // 편집 취소는 원래 문서로 복귀. 뷰어→편집이 같은 웹뷰 안 이동이라
      // 여기서 BACK 을 보내면 문서로 돌아가는 대신 웹뷰가 통째로 닫힘.
      // 신규 작성의 복귀처는 PC 목록이라 앱에서는 BACK 이 맞음.
      if (isEditMode && documentId) {
        router.push(buildViewPath(documentId));
        return;
      }
      sendToRN(RnMessage.BACK);
      return;
    }
    router.push(exitDestination());
  };

  const handleCancel = () => {
    if (buttonsLocked) return;
    if (!shouldWarnOnExit()) {
      exitToList();
      return;
    }
    dialog.show({
      message: t('작성을 취소하시겠습니까?'),
      confirmButtonText: t('확인'),
      cancelButtonText: t('취소'),
      onConfirm: exitToList,
    });
  };

  const submitTemplate = async () => {
    setIsTemplateFlowLocked(true);
    try {
      // 빈 본문은 null 전송 — 서버 명세상 템플릿 비우기 값
      const content = getValues('content') ?? '';
      await saveTemplate(
        apvType,
        isEmptyContent(content) ? null : stripFontFamily(content),
      );
      snackbar.showSuccess({ message: t('템플릿이 저장되었습니다.') });
    } catch (e) {
      snackbar.showError({
        message: messageForApiError(
          e,
          t('템플릿 저장에 실패했습니다.'),
          false,
          t,
        ),
      });
    } finally {
      setIsTemplateFlowLocked(false);
    }
  };

  // 전 법인 공용 양식을 덮어쓰므로 확인 후 저장
  const handleSaveTemplate = () => {
    if (templateButtonLocked) return;
    const typeName =
      pickI18nText(approvalType?.name, normalizeLang(i18n.language)) ||
      String(apvType);
    dialog.show({
      title: t('템플릿 저장'),
      message: t(
        '{{name}} 양식의 템플릿을 현재 본문으로 저장합니다.\n기존 템플릿은 덮어쓰기되며, 모든 법인의 새 작성 화면에 바로 적용됩니다.\n계속하시겠습니까?',
        // 양식명에 & 가 들어가면(F&CO) 기본 escape 로 &amp; 가 그대로 보임
        { name: typeName, interpolation: { escapeValue: false } },
      ),
      confirmButtonText: t('저장'),
      cancelButtonText: t('취소'),
      onConfirm: submitTemplate,
    });
  };

  // 결재시작 흐름 중에는 결재시작 버튼에만 spinner, 임시저장 흐름 중에는 임시저장 버튼에만.
  const isDraftSpinnerVisible = isDraftFlowLocked;
  const isSubmitSpinnerVisible = isSubmitting && isSubmitFlowLocked;

  // PORTAL-13609 는 MO 한정 티켓 — PC 는 기존 M/아이콘 크기 유지
  const actionButtonSize = isMobile ? 'large' : 'medium';
  const templateIconSize = isMobile ? 16 : 24;
  // send.svg 는 글리프가 24 박스의 60% — PC 12 은 이전 아이콘(90%) 기준이라 그대로 두면 작아짐
  const submitIconSize = isMobile ? 24 : 20;

  return (
    <Footer>
      <LeftActionStack>
        {/* 모바일 이탈은 상단 뒤로가기가 담당 — 취소 버튼 미노출 */}
        {!isMobile && (
          <BoxButton
            variant="caution"
            disabled={buttonsLocked}
            onClick={handleCancel}
          >
            {t('작성 취소')}
          </BoxButton>
        )}
        {isPortalAdmin && (
          <TextButton
            variant="caution"
            underline
            disabled={templateButtonLocked}
            startIcon={
              isTemplateFlowLocked ? (
                <Spinner theme="black" size={`${templateIconSize}px`} />
              ) : (
                <FileUploadIcon
                  width={templateIconSize}
                  height={templateIconSize}
                />
              )
            }
            onClick={handleSaveTemplate}
          >
            {t('템플릿 저장(admin)')}
          </TextButton>
        )}
      </LeftActionStack>
      <ActionButtonGroup>
        {/* SA 전표(99)·SAP 중간 관리 수수료 정산(157)·도급비 수수료 정산(167)·채권현황(185)·SA 출장비 정산(177)·사전 신청(179)·예산증액(187)·담보 신규(154)·변경(155)·회계거래처(101)·인플루언서 월마감(108)·PPL 정산(135)·GERP 생산거래처(120)·STYLE MASTER 가격 변경(128)·Sales Contract(136)·저장품 추가발주(184)·CREDIT NOTE(156)·PLM(123)은 레거시부터 임시저장 없이 상신만 */}
        {!isSaVoucherApprovalType(apvType) &&
          !isShopManagerFeeType(apvType) &&
          !isSapContractFeeType(apvType) &&
          !isSapBondStatusType(apvType) &&
          !isSaTravelType(apvType) &&
          !isSapBudgetIncreaseType(apvType) &&
          !isSapCollateralType(apvType) &&
          !isAccountingBpType(apvType) &&
          !isInfluencerMonthlyReportType(apvType) &&
          !isInfluencerPplType(apvType) &&
          !isProductionVendorType(apvType) &&
          !isProductLifecycleType(apvType) &&
          !isStyleMasterPriceType(apvType) &&
          !isSalesContractType(apvType) &&
          !isInventoryReplenishmentType(apvType) &&
          !isCreditNoteType(apvType) && (
            <BoxButton
              variant="secondary"
              size={actionButtonSize}
              disabled={buttonsLocked}
              startIcon={
                isDraftSpinnerVisible ? (
                  <Spinner theme="black" size="16px" />
                ) : undefined
              }
              onClick={handleDraft}
            >
              {t('임시저장')}
            </BoxButton>
          )}
        <BoxButton
          variant="primary"
          size={actionButtonSize}
          disabled={buttonsLocked}
          startIcon={
            isSubmitSpinnerVisible ? (
              <Spinner theme="black" size="16px" />
            ) : (
              <SendIcon width={submitIconSize} height={submitIconSize} />
            )
          }
          onClick={() => handleSubmitFlow()}
        >
          {t('저장 | 결재시작')}
        </BoxButton>
      </ActionButtonGroup>
    </Footer>
  );
};

export default CreateFooter;
